import { useDeck } from "../../lib/deck-context";
import { X, Plus } from "lucide-react";
import { BASE, BUDGET, fmtK, fmtM, fmtMoney, fmtPct } from "../../lib/model";
import { Card, Eyebrow, Scale } from "../deck/primitives";
import { Waterfall } from "../deck/Waterfall";
import { stageNumbers } from "./exitData";

const Stat = ({ label, value, accent, testid }) => (
  <div className={`rounded-xl p-5 ${accent ? "bg-arq text-white" : "bg-paper"}`}>
    <div className={`text-[11px] ${accent ? "text-white/75" : "text-steel"}`}>{label}</div>
    <div data-testid={testid} className={`mt-2 font-display text-2xl tabular-nums ${accent ? "" : "text-ink"}`}>{value}</div>
  </div>
);

const PRICE_KEY = { control: "priceControl", pad: "pricePad", buildReady: "priceBuildReady" };

const PriceInput = ({ stageKey, st }) => {
  const { setInput } = useDeck();
  const k = PRICE_KEY[stageKey];
  return (
    <span className="flex flex-col items-end gap-1">
      <span className="flex items-center gap-1 rounded-lg border border-white/40 bg-white/10 px-2.5 py-1 focus-within:border-white focus-within:bg-white/15 transition-colors">
        <span className="text-xs text-white/70">$</span>
        <input
          data-testid={`exit-price-input-${stageKey}`}
          type="number"
          step={10000}
          min={0}
          value={Math.round(st.assignment)}
          onChange={(e) => setInput(k, e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))}
          className="w-[110px] text-right tabular-nums text-white bg-transparent outline-none"
        />
      </span>
      {st.overridden ? (
        <button data-testid={`exit-price-reset-${stageKey}`} onClick={() => setInput(k, null)} className="text-[11px] text-white/70 underline underline-offset-2 hover:text-white">
          Your price · reset to modeled {fmtMoney(st.modeled)}
        </button>
      ) : <span className="text-[11px] text-white/60">Modeled estimate · type your price</span>}
    </span>
  );
};

const ProfitBanner = ({ s, n, m, a }) => {
  const parts =
    s.key === "sell"
      ? [["Sell the stabilized asset for", m.netSale, "after sale costs"], ["Less total development cost", -a.tdc]]
      : s.key === "hold"
      ? [["Stabilized value", m.value], ["Less total development cost", -a.tdc], ["Less refinance costs", -m.refiCosts]]
      : [["Sell / assign the position for", n.value, null, true], ["Less money spent to reach this stage", -n.cum], ["Less transaction costs", -m.stages[s.key].friction]];
  return (
    <div data-testid={`exit-profit-banner-${s.key}`} className="mt-8 rounded-2xl bg-arq text-white p-7 lg:p-8 grid lg:grid-cols-12 gap-6 items-center">
      <div className="lg:col-span-4">
        <div className="font-mono text-[11px] tracking-[0.22em] text-white/75">ESTIMATED PROFIT · THIS EXIT</div>
        <div data-testid="exit-profit-value" className="mt-2 font-display text-6xl tabular-nums leading-none">≈ {fmtMoney(n.created)}</div>
        <div className="mt-3 text-sm text-white/80">
          {s.key === "hold" ? `Includes ${fmtM(m.cashOut)} cash-out at refinance + ownership retained` : `${(n.roc * 100).toFixed(0)}% return on ${fmtMoney(n.cash)} cash invested`} · ~{n.months} months from today
        </div>
      </div>
      <div className="lg:col-span-8 lg:border-l lg:border-white/25 lg:pl-8 space-y-2.5">
        {parts.map(([l, v, sub, editable]) => (
          <div key={l} className="flex justify-between items-center gap-4 text-[15px]">
            <span className="text-white/85">{l}{sub && <span className="text-white/55"> ({sub})</span>}</span>
            {editable ? <PriceInput stageKey={s.key} st={m.stages[s.key]} /> : <span className="tabular-nums">{v < 0 ? "− " : ""}{fmtMoney(Math.abs(v))}</span>}
          </div>
        ))}
        <div className="flex justify-between gap-4 pt-2.5 border-t border-white/25 text-[15px] font-semibold">
          <span>Profit</span><span className="tabular-nums">{fmtMoney(n.created)}</span>
        </div>
      </div>
    </div>
  );
};

const LateStage = ({ s }) => {
  const { m, inputs } = useDeck();
  if (s.key === "sell") {
    return (
      <Waterfall testid="exit-sell-waterfall" height={260} decimals={2} steps={[
        { label: "Base stabilized value", value: m.value, kind: "total" },
        { label: `Sale costs (${inputs.saleCost}%)`, value: m.saleCosts, kind: "sub" },
        { label: "Net sale proceeds", value: m.netSale, kind: "total" },
        { label: "Total Development Cost", value: inputs.tdc, kind: "sub" },
        { label: `Gross project profit · ${fmtPct(m.poc * 100, 1)} on cost`, value: m.profit, kind: "total" },
      ]} />
    );
  }
  return (
    <Waterfall testid="exit-hold-waterfall" height={260} decimals={2} steps={[
      { label: `${inputs.ltv}% LTV refinance`, value: m.refiLoan, kind: "total" },
      { label: "Construction debt payoff", value: m.debt, kind: "sub" },
      { label: `Refinance costs (${inputs.refiCost}%)`, value: m.refiCosts, kind: "sub" },
      { label: "Modeled net cash-out", value: m.cashOut, kind: "total" },
    ]} />
  );
};

const ITEM_KEY = { control: "itemsControl", pad: "itemsPad", buildReady: "itemsBuildReady" };
const CATS = BUDGET.map((b) => [b.key, b.label.split(" / ")[0]]);

const ItemRow = ({ k, it, base }) => {
  const { setItem, removeItem } = useDeck();
  const changed = !base || base.amount !== it.amount;
  return (
    <div data-testid={`exit-item-row-${it.id}`} className="group flex items-center justify-between gap-3 py-2 border-b border-line/70 text-sm">
      <div className="flex-1 min-w-0">
        {it.custom ? (
          <input data-testid={`exit-item-label-${it.id}`} value={it.label} onChange={(e) => setItem(k, it.id, { label: e.target.value })}
            className="w-full bg-transparent text-char border-b border-dashed border-line focus:border-arq outline-none py-0.5" />
        ) : <span className="text-char">{it.label}</span>}
        {it.custom && (
          <select data-testid={`exit-item-cat-${it.id}`} value={it.cat} onChange={(e) => setItem(k, it.id, { cat: e.target.value })}
            className="mt-1 text-[11px] text-steel bg-transparent outline-none">
            {CATS.map(([v, l]) => <option key={v} value={v}>{`Budget line: ${l}`}</option>)}
          </select>
        )}
      </div>
      <span className="flex items-center gap-2 shrink-0">
        {base && changed && <span className="text-[11px] text-steel line-through tabular-nums">{fmtK(base.amount)}</span>}
        {!base && <span className="text-[10px] font-mono tracking-[0.12em] text-arq">ADDED</span>}
        <span className={`flex items-center gap-1 rounded-lg border px-2.5 py-1 transition-colors focus-within:border-arq ${changed ? "border-arq/50 bg-arq-soft" : "border-line bg-white hover:border-arq/40"}`}>
          <span className="text-xs text-steel">$</span>
          <input data-testid={`exit-item-input-${it.id}`} type="number" step={5000} min={0} value={it.amount}
            onChange={(e) => setItem(k, it.id, { amount: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) })}
            className="w-[84px] text-right tabular-nums text-ink bg-transparent outline-none" />
        </span>
        <button data-testid={`exit-item-remove-${it.id}`} onClick={() => removeItem(k, it.id)} title="Remove line"
          className="h-7 w-7 grid place-items-center rounded-full text-steel hover:text-[#8A3340] hover:bg-[#F8EDEE] transition-colors">
          <X size={14} />
        </button>
      </span>
    </div>
  );
};

const LineItems = ({ k }) => {
  const { raw, addItem } = useDeck();
  const key = ITEM_KEY[k];
  const items = raw[key];
  const baseSum = BASE[key].reduce((t, v) => t + v.amount, 0);
  const total = items.reduce((t, v) => t + v.amount, 0);
  const delta = total - baseSum;
  const removed = BASE[key].filter((b) => !items.some((x) => x.id === b.id));
  return (
    <div data-testid={`exit-line-items-${k}`}>
      {items.map((it) => <ItemRow key={it.id} k={key} it={it} base={BASE[key].find((b) => b.id === it.id)} />)}
      {removed.length > 0 && <div className="py-2 text-[11px] text-steel">Removed: {removed.map((r) => `${r.label} (${fmtK(r.amount)})`).join(" · ")}</div>}
      <button data-testid={`exit-item-add-${k}`} onClick={() => addItem(key)} className="mt-3 flex items-center gap-2 rounded-full border border-dashed border-arq/50 px-4 py-1.5 text-xs text-arq hover:bg-arq-soft transition-colors">
        <Plus size={13} /> Add line item
      </button>
      <div className="flex justify-between gap-4 pt-3 text-sm">
        <span className="text-steel">Stage subtotal{delta !== 0 && <span data-testid={`exit-items-delta-${k}`} className="ml-2 text-arq">({delta < 0 ? "saving " : "+"}{fmtK(Math.abs(delta))} vs. base)</span>}</span>
        <span className="tabular-nums text-ink font-medium">{fmtK(total)}</span>
      </div>
      <p className="mt-2 text-xs text-steel">Edits flow through to stage cost, the matching Development Budget line, Total Development Cost and every exit profit.</p>
    </div>
  );
};

export const StageDetail = ({ s }) => {
  const { m, inputs } = useDeck();
  const n = stageNumbers(s, m, inputs);
  const late = s.key === "sell" || s.key === "hold";
  return (
    <Card data-testid={`exit-detail-${s.key}`} className="p-8 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <div className="font-mono text-[11px] tracking-[0.2em] text-arq">EXIT {s.n}{s.tag && ` · ${s.tag.toUpperCase()}`}</div>
          <div className="mt-2 font-display text-3xl lg:text-4xl text-ink">{s.title}</div>
          <p className="mt-3 text-char/80">{s.goal}</p>
        </div>
      </div>
      <ProfitBanner s={s} n={n} m={m} a={inputs} />
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Cumulative investment" value={fmtMoney(n.cum)} testid="exit-stat-cumulative" />
        <Stat label="Incremental cost to reach stage" value={fmtMoney(n.inc)} testid="exit-stat-incremental" />
        <Stat label="Remaining development cost" value={fmtMoney(n.remaining)} testid="exit-stat-remaining" />
        <Stat label={n.valueLabel} value={fmtM(n.value)} testid="exit-stat-value" />
        <Stat label={n.createdLabel} value={fmtMoney(n.created)} testid="exit-stat-created" />
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          {late ? (
            <>
              <Eyebrow className="mb-6">{s.key === "sell" ? "Build + Sell Waterfall" : "Refinance Waterfall"}</Eyebrow>
              <LateStage s={s} />
              {s.key === "hold" && (
                <div className="mt-8 rounded-xl bg-ink-deep text-white p-6 flex flex-wrap items-center justify-between gap-4">
                  <div><div className="text-xs text-white/60">Remaining project equity</div><div data-testid="exit-hold-retained" className="font-display text-3xl tabular-nums">≈ {fmtM(m.retained)}</div></div>
                  <div className="text-xs text-white/70 max-w-sm">Plus continued ownership, recurring income, potential rent growth, future appreciation, future refinance opportunities and future sale optionality.</div>
                </div>
              )}
            </>
          ) : (
            <>
              <Eyebrow className="mb-3">{s.key === "control" ? "Estimated cumulative predevelopment spend" : "Incremental work"} · {fmtK(n.inc)}</Eyebrow>
              <LineItems k={s.key} />
              {s.explain && <p className="mt-5 text-sm text-ink border-l-2 border-arq pl-4">{s.explain}</p>}
              {s.monetization && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.monetization.map((t) => <span key={t} className="rounded-full border border-line px-3.5 py-1.5 text-xs text-char">{t}</span>)}
                </div>
              )}
            </>
          )}
        </div>
        <div className="lg:col-span-5 space-y-7">
          <div className="space-y-4">
            {[["Remaining development risk (to buyer)", s.risk.dev, "dev"], ["Construction risk borne by JV", s.risk.construction, "construction"], ["Capital at risk", s.risk.capital, "capital"], ["Time / complexity", s.risk.time, "time"]].map(([l, v, k]) => (
              <div key={l} className="flex items-center justify-between gap-4">
                <span className="text-sm text-char">{l}</span><Scale value={v} testid={`exit-risk-${k}`} />
              </div>
            ))}
          </div>
          {[["What is being sold", s.sold], ["What is retained", s.retained], ["What the next investor still needs to execute", s.nextInvestor]].map(([t, d]) => (
            <div key={t} className="border-t border-line pt-4">
              <Eyebrow>{t}</Eyebrow>
              <p className="mt-2 text-sm text-char leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
